import { memo, useEffect, useMemo, useState } from 'react';
import { floorToBucket, timeframeToSeconds } from '../lib/candleAggregation';

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

/**
 * Renders lightweight anchored annotations that stick to price/time.
 */
export const AnnotationLayer = memo(function AnnotationLayer({ containerRef, annotations, getCoordinates, viewportVersion, timeframe = 'Tick' }) {
    const [positions, setPositions] = useState([]);

    useEffect(() => {
        if (timeframe !== 'Tick') {
            setPositions([]);
            return;
        }
        const container = containerRef.current;
        if (!container || !getCoordinates) {
            return;
        }
        const width = container.clientWidth;
        const height = container.clientHeight;
        const next = [];
        for (const annotation of annotations) {
            const coords = resolveCoordinatesForAnnotation({
                annotation,
                timeframe,
                getCoordinates
            });
            if (!coords || !Number.isFinite(coords.x) || !Number.isFinite(coords.y)) {
                continue;
            }
            if (coords.x < -20 || coords.x > width + 20 || coords.y < -20 || coords.y > height + 20) {
                continue;
            }
            next.push({
                id: annotation.id,
                label: annotation.label,
                tone: annotation.tone,
                kind: annotation.kind,
                price: annotation.price,
                time: annotation.time,
                x: coords.x,
                y: coords.y
            });
        }
        setPositions(next);
    }, [annotations, containerRef, getCoordinates, timeframe, viewportVersion]);

    if (timeframe !== 'Tick') {
        return null;
    }

    const rendered = useMemo(() => positions.map((position) => {
        const tone = position.tone === 'down' ? 'down' : 'up';
        const arrow = tone === 'down' ? '▼' : '▲';
        return (
            <div
                key={position.id}
                className="chart-annotation"
                data-tone={tone}
                style={{
                    left: `${position.x}px`,
                    top: `${position.y}px`,
                }}
                data-kind={position.kind || "entry"}
            >
                <span className="chart-annotation__arrowbox">
                    <span className="chart-annotation__arrow">{arrow}</span>
                </span>
            </div>
        );
    }), [positions]);

    return <>{rendered}</>;
});

function resolveCoordinatesForAnnotation({ annotation, timeframe, getCoordinates }) {
    if (!annotation || !getCoordinates || !Number.isFinite(annotation.time) || !Number.isFinite(annotation.price)) {
        return null;
    }

    const directCoordinates = getCoordinates(annotation.price, annotation.time);
    if (directCoordinates) {
        return directCoordinates;
    }

    const intervalSeconds = timeframe === 'Tick' ? 0.3 : timeframeToSeconds(timeframe);
    const bucketTime = floorToBucket(annotation.time, intervalSeconds);
    const candidateTimes = [
        bucketTime,
        bucketTime + intervalSeconds,
        bucketTime - intervalSeconds
    ];

    for (const candidateTime of candidateTimes) {
        if (!Number.isFinite(candidateTime)) {
            continue;
        }
        const candidateCoordinates = getCoordinates(annotation.price, candidateTime);
        if (candidateCoordinates) {
            return candidateCoordinates;
        }
    }

    return null;
}
